import { useTheme } from '@emotion/react'
import { Box } from '@mui/system'
import { ColDef } from 'ag-grid-community'
import { useState, useEffect } from 'react'
import MedAgDataGrid from '../../components/MedAgDataGrid'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import { ProrithmDevices } from '../ProRithm/Constants'
import { cancelLabOrder, getLabInvoices } from '../../services/LmsService'
import MaskedNumber from '../../components/MaskedNumber'
import ProvenanceView from '../../components/ProvenanceView'
import { startSxpProxy } from '../../utils/api'
import { ADMIN_PROJECT_ID } from '../../utils/constants'
import {
  odooPharmacyLink,
  PageSize,
  paid,
  SaleOrderOdooLink,
} from '../appointments/models'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Backdrop,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  cancel,
  INVOICE_STATUS,
  invoiceData,
  lowerCasePaid,
  noInvoiceText,
  noTestsFound,
  NotPaid,
  opd,
  orderMsg,
  optionValue,
  ORDER_INVOICE_TEXT,
  partial,
  partialUpperCase,
  SaleOrder,
} from './models'
import { unpaid } from '../OPDScreen/constant'
import { labordercreate } from '../bedManagement/endpoints/EndPoints'
import { useAppSelector } from '../../app/hooks'
import { selectQuery, selectParam } from '../membership/membershipSlice'
import { Cancelled } from '../membership/models'
import {
  invoice,
  loadingMessage,
  saleOrder,
  testsLowercase,
} from '../lms/constants'
import { viewButtons } from '../lms/components/admin/constants'
import { format } from 'date-fns'
import { toast } from 'react-toastify'

const styles = () => ({
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': { flex: 1 },
  },
  unpaidLink: {
    textTransform: 'initial',
    background: '#E35335',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': { background: '#E35335' },
  },
  noInvoiceLink: {
    textTransform: 'initial',
    background: 'grey',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': { background: 'yellow' },
  },
  paidLink: {
    textTransform: 'initial',
    background: 'green',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': { background: 'green' },
  },
  partialLink: {
    textTransform: 'initial',
    background: 'orange',
    color: 'white',
    padding: '0px 2px',
    fontSize: '10px',
    '&:hover': { background: 'orange' },
  },
  endIcon: { fontSize: '13px !important' },
  MedAgDataGridInput: {
    border: '1px solid #aeafb5',
    borderRadius: 5,
    padding: '5px !important',
    background: 'white',
  },
  backDrop: {
    color: '#fff',
    zIndex: 9999,
  },
  globalSearchInput: {
    minWidth: '300px !important',
    backgroundColor: 'white !important',
    fontSize: '10px',
    padding: '0px !important',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#aeafb5',
      },
      '&:hover fieldset': {
        borderColor: '#aeafb5 !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#aeafb5 !important',
        borderWidth: '1px !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: '4px 10px',
    },
    '& .MuiInputBase-adornedStart': {
      padding: '0px !important',
    },
  },
  expandClass: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  expandText: {
    wordBreak: 'break-word',
    fontSize: '12px',
    lineHeight: 1.2,
  },
  expandContent: {
    color: 'blue',
    marginLeft: '3px',
  },
})

const OrderInvoice = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const navigate = useNavigate()
  const query = useAppSelector(selectQuery)
  const param = useAppSelector(selectParam)
  const [invoices, setInvoices] = useState<ProrithmDevices[]>([])
  const [isSaleOrder, setIsSaleOrder] = useState('invoice')
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [page, setPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredInvoices = invoices?.filter((item: any) => {
    const target = [
      item?.name,
      item?.partner_id?.name,
      item?.partner_name,
      item?.uhid,
      item?.partner_id?.ref,
      item?.consumer_ids?.join(', '),
      item?.partner_id?.phone,
      item?.partner_phone,
    ]
      ?.join(' ')
      ?.toLowerCase()

    return target?.includes(searchTerm?.toLowerCase())
  })

  const fetchData = async () => {
    setIsLoading(true)
    const params = {
      ref_prefix:
        isSaleOrder !== ORDER_INVOICE_TEXT?.INVOICETEXT
          ? optionValue?.b2b?.value
          : optionValue?.LAB?.value,
      doc_type:
        isSaleOrder !== ORDER_INVOICE_TEXT?.INVOICETEXT
          ? SaleOrder
          : invoiceData,
      limit: pageSize,
      offset: page * pageSize,
    }
    try {
      const response = await getLabInvoices(params)
      const data = response?.data?.data || []
      const total = response?.data?.total_count || 0
      setInvoices(data)
      setTotalCount(total)
      const totalPages = Math.ceil(total / pageSize)
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (limit !== pageSize) {
      setLimit(pageSize)
      setCurrentPage(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, limit])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, isSaleOrder, page, query, param])

  const handlePaymentLink = (params: any) => {
    const d = params?.data
    if (!d) return null

    const isUnpaid =
      d?.payment_state === NotPaid || d?.payment_status === NotPaid
    const isCancelled = d?.state === cancel
    const isPartial =
      d?.payment_state === partial || d?.payment_status === partial
    const noInvoice = saleOrder === SaleOrder && d?.payment_status === false
    const isPaid =
      d?.payment_status === lowerCasePaid || d?.payment_state === lowerCasePaid
    const getStatus = () => {
      switch (true) {
        case isCancelled:
          return INVOICE_STATUS?.CANCELLED
        case isUnpaid:
          return INVOICE_STATUS?.UNPAID
        case noInvoice:
          return INVOICE_STATUS?.NOINVOICE
        case isPartial:
          return INVOICE_STATUS?.PARTIAL
        case isPaid:
          return INVOICE_STATUS?.PAID
        default:
          return INVOICE_STATUS?.UNKNOWN
      }
    }
    const status = getStatus()

    const getButtonClass = () => {
      switch (status) {
        case INVOICE_STATUS?.CANCELLED:
          return classes?.cancelLink
        case INVOICE_STATUS?.UNPAID:
          return classes?.unpaidLink
        case INVOICE_STATUS?.NOINVOICE:
          return classes?.noInvoiceLink
        case INVOICE_STATUS?.PARTIAL:
          return classes?.partialLink
        case INVOICE_STATUS?.PAID:
          return classes?.paidLink
        default:
          return ''
      }
    }

    const getEndIcon = () => {
      switch (status) {
        case INVOICE_STATUS?.CANCELLED:
        case INVOICE_STATUS?.UNPAID:
        case INVOICE_STATUS?.PARTIAL:
        case INVOICE_STATUS?.NOINVOICE:
          return <OpenInNewIcon className={classes?.endIcon} />
        case INVOICE_STATUS?.PAID:
          return <CheckCircleOutlineIcon className={classes?.endIcon} />
        default:
          return null
      }
    }

    const getButtonLabel = () => {
      switch (status) {
        case INVOICE_STATUS?.NOINVOICE:
          return noInvoiceText
        case INVOICE_STATUS?.CANCELLED:
          return Cancelled
        case INVOICE_STATUS?.UNPAID:
          return unpaid
        case INVOICE_STATUS?.PARTIAL:
          return partialUpperCase
        case INVOICE_STATUS?.PAID:
          return paid
        default:
          return '-'
      }
    }

    const handleClick = () => {
      if (d.service === opd) {
        openOdooInvoice(d?.id)
      } else {
        OpenSaleOrder(d?.id)
      }
    }

    return (
      <Button
        size='small'
        onClick={handleClick}
        className={getButtonClass()}
        endIcon={getEndIcon()}
      >
        {getButtonLabel()}
      </Button>
    )
  }

  const openOdooInvoice = (id: any) => {
    startSxpProxy(ADMIN_PROJECT_ID, odooPharmacyLink, { invoiceId: id })
      ?.then((data) => {
        const url = data?.data?.auth_link
        window.open(url, '_blank', 'noopener,noreferrer')
      })
      .catch((err) => console.error('Error opening invoice:', err))
  }
  const OpenSaleOrder = (id: any) => {
    startSxpProxy(ADMIN_PROJECT_ID, SaleOrderOdooLink, { invoiceId: id })
      ?.then((data) => {
        const url = data?.data?.auth_link
        window.open(url, '_blank', 'noopener,noreferrer')
      })
      .catch((err) => console.error('Error opening invoice:', err))
  }
  const handleInvoiceNo = (params: any) => {
    return params?.data?.invoices?.length > 0
      ? params?.data?.invoices?.map((e: any, i: number) => (
          <ul key={i}>
            <li>
              {e?.invoice_name}-{e?.invoice_status}
            </li>
          </ul>
        ))
      : '-'
  }
  const handlecancel = async (orderRef: string) => {
    setIsLoading(true)
    try {
      await cancelLabOrder(optionValue?.origin?.label, orderRef)
      toast.success(orderMsg?.ordercancelled)
    } catch (error) {
      console.error(orderMsg?.errorcancel, error)
    } finally {
      setIsLoading(false)
    }
  }
  const renderOrderDate = (params: any) => {
    const rawDate =
      isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT
        ? params?.data?.invoice_date_time
        : params?.data?.date_order
    if (!rawDate) return '-'
    const originalDate = new Date(rawDate)
    const offsetDate = new Date(originalDate.getTime() + 5.5 * 60 * 60 * 1000)
    return format(offsetDate, 'dd-MM-yyyy HH:mm')
  }
  const renderDueAmount = (params: any) => {
    return isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT
      ? params?.data?.amount_residual_signed ?? '-'
      : params?.data?.due_amount ?? '-'
  }
  const renderStatus = (params: any) => {
    return isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT
      ? params?.data?.state ?? '-'
      : params?.data?.status ?? '-'
  }
  const useRenderTests = (params: any) => {
    const checkLength = params?.data?.invoice_line_ids?.length || 0
    const [expand, setExpand] = useState(false)
    return checkLength === 0 ? (
      noTestsFound
    ) : (
      <>
        {expand ? (
          <div className={classes?.expandClass}>
            {params?.data?.invoice_line_ids?.map((e: any, i: number) => (
              <li key={i}>
                <span className={classes?.expandText}>{e?.name}</span>
              </li>
            ))}
          </div>
        ) : (
          <li>{params?.data?.invoice_line_ids?.[0]?.name}</li>
        )}
        <span
          onClick={() => setExpand(!expand)}
          className={classes?.expandContent}
        >
          {params?.data?.invoice_line_ids?.length > 1
            ? expand
              ? viewButtons?.viewLess
              : viewButtons?.viewMore
            : ''}
        </span>
      </>
    )
  }
  const renderCancelCell = (
    params: any,
    handleCancel: (ref: string) => void
  ) => {
    const isInvoice = params?.data?.type_name === ORDER_INVOICE_TEXT?.INVOICE
    const isPosted = params?.data?.payment_state === NotPaid

    if (!isInvoice) return null

    const className = isPosted ? 'cancel-enabled' : 'cancel-disabled'

    return (
      <span
        className={className}
        onClick={() => {
          if (isPosted) {
            handleCancel(params?.data?.ref)
          }
        }}
      >
        Cancel
      </span>
    )
  }
  const columnDefs: ColDef[] = [
    {
      headerName: 'Sl no',
      valueGetter: (params: any) =>
        params.node ? params.node.rowIndex + 1 : null,
      width: 80,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Order Id',
      field: 'name',
      width: isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT ? 250 : 150,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Order Type',
      field: 'name',
      width: 150,
      cellRenderer: (params: any) =>
        params?.data?.type_name === ORDER_INVOICE_TEXT?.INVOICE
          ? ORDER_INVOICE_TEXT?.INVOICE
          : ORDER_INVOICE_TEXT?.SALE_ORDER,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Order Date',
      field: 'date_order',
      width: 150,
      cellRenderer: renderOrderDate,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'UHID',
      field: 'uhid',
      minWidth: 200,
      flex: 1,
      valueGetter: (params: any) =>
        params?.data?.partner_id?.ref ||
        params?.data?.consumer_ids?.join(', ') ||
        '',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Name',
      field: 'partner_id.name',
      width: 200,
      valueGetter: (params: any) =>
        params?.data?.partner_id?.name || params?.data?.partner_name,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Mobile No',
      field: 'partner_id.phone',
      width: 150,
      valueGetter: (params: any) =>
        params?.data?.partner_id?.phone || params?.data?.partner_phone,
      cellRenderer: (params: any) =>
        params.value ? <MaskedNumber mobile={params.value} /> : '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Tests',
      field: 'tests',
      minWidth: 200,
      cellRenderer: useRenderTests,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      autoHeight: true,
      wrapText: true,
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
      },
    },
    {
      headerName: 'Service',
      field: 'service',
      width: 200,
      valueGetter: (params: any) =>
        params?.data?.service || params?.data?.partner_phone,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Operating Unit',
      field: 'operating_unit_id',
      width: 200,
      cellRenderer: (params: any) => {
        const value = params?.data?.operating_unit_id
        let displayText = 'Loc'

        if (Array.isArray(value)) {
          displayText = value?.[1] || 'Loc'
        } else if (typeof value === 'string') {
          displayText = value
        }
        return displayText?.replace(/^\[[^\]]+\]\s*/, '') || 'Loc'
      },
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Total',
      field: 'amount_total',
      width: 150,
      cellRenderer: (params: any) => params?.data?.amount_total ?? '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Amount Due',
      field: 'due',
      width: 150,
      cellRenderer: renderDueAmount,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Pay Status',
      field: 'payment_state',
      width: 150,
      cellRenderer: handlePaymentLink,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Status',
      field: 'statuses',
      width: 100,
      cellRenderer: renderStatus,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Cancel',
      field: 'cancel',
      width: 100,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      cellRenderer: (params: any) => renderCancelCell(params, handlecancel),
    },
    {
      headerName: 'Invoice No',
      field: 'invoiceNo',
      width: 250,
      headerClass: classes?.centerHeader,
      cellRenderer: handleInvoiceNo,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    {
      headerName: 'Invoice Status',
      field: 'invoiceStatus',
      width: 100,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) =>
        params?.data?.invoices?.[0]?.invoice_status ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Created On',
      field: 'createdOn',
      minWidth: 200,
      cellRenderer: (params: any) => {
        const data = params?.data
        const rawDate =
          data?.invoice_date_time ?? data?.create_date ?? data?.date_order
        // const rawDate=data?.invoice_date_time
        if (!rawDate) return '-'
        const originalDate = new Date(rawDate)
        const offsetDate = new Date(
          originalDate?.getTime() + (5 * 60 + 30) * 60 * 1000
        )
        const date = offsetDate?.toDateString()
        const time = offsetDate?.toLocaleTimeString('en-IN', {
          hour12: false,
        })
        return (
          <ProvenanceView
            name={data?.thop_user_name || data?.create_uid?.[1] || '-'}
            date={`${date} ${time}`}
            mode='CREATE'
          />
        )
      },
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'Updated On',
      field: 'updatedOn',
      minWidth: 200,
      cellRenderer: (params: any) =>
        isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT
          ? params?.data?.write_uid?.[1]
          : params?.data?.write_uid ?? '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        display: 'flex',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
  ]
  const filteredColumnDefs =
    isSaleOrder === ORDER_INVOICE_TEXT?.INVOICETEXT
      ? columnDefs?.filter(
          (e: any) =>
            e?.field !== ORDER_INVOICE_TEXT?.INVOICE_STATUS &&
            e?.field !== ORDER_INVOICE_TEXT?.INVOICE_NUMBER_TEXT
        )
      : columnDefs?.filter(
          (e: any) => e?.field !== testsLowercase && e?.field !== cancel
        )
  const FilterActions = () => {
    return (
      <>
        <button
          data-testid='create-button'
          className='btn btn-primaryBtn ml10'
          onClick={() => navigate(`${labordercreate}/id`)}
        >
          Create
        </button>
      </>
    )
  }

  return (
    <>
      <div className='layoutcontent total-width-content'>
        {isLoading && (
          <Backdrop className={classes?.backDrop} open={isLoading}>
            <Typography variant='h6' color='whitesmoke'>
              {loadingMessage}
            </Typography>
            <CircularProgress color='inherit' />
          </Backdrop>
        )}
        <Box height={'100%'} width={'100%'}>
          <MedAgDataGrid
            tableHeading='Orders'
            tableHeight={'100%'}
            rowData={filteredInvoices}
            columnDefs={filteredColumnDefs}
            exportIcon={true}
            pagination
            pageSizeOptions={PageSize}
            setPage={setPage}
            page={page}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalCount}
            headerHeight={30}
            filterActions={
              <>
                <select
                  data-testid='order-type-select'
                  onChange={(e) => setIsSaleOrder(e.target.value)}
                  className={classes?.MedAgDataGridInput}
                >
                  <option value={invoice}>{ORDER_INVOICE_TEXT?.INVOICE}</option>
                  <option value={saleOrder}>
                    {ORDER_INVOICE_TEXT?.SALE_ORDER}
                  </option>
                </select>

                <TextField
                  placeholder='Search Orders'
                  value={searchTerm}
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                  className={classes?.globalSearchInput}
                />
                <FilterActions />
              </>
            }
            searchField={false}
          />
        </Box>
      </div>
    </>
  )
}

export default OrderInvoice
